'use client';

import { useState, useSyncExternalStore } from 'react';
import { Flag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';
import { currentEnvironment } from '@/lib/config/environment';
import {
  EXTERNAL_CORRESPONDENCE_IMPORT_FLAG,
  OUTLOOK_SENT_ITEMS_SYNC_FLAG
} from '@/lib/services/communications';
import { useFeatureFlags } from '@/lib/providers/feature-flags-provider';
import { cn } from '@/lib/utils';

/**
 * The flags the panel offers, and the panel's whole content.
 *
 * Exported so the spec can enumerate the set from here rather than restate it:
 * the "feature flags only" assertion (VALE-1378) counts switches against this
 * length, and a second copy of the list would let the two drift.
 */
export const KNOWN_FLAGS = [
  // Importing external correspondence into a Communications thread (VALE-597). Ships disabled
  // and is enabled per firm on dev, so this panel is how a developer or QA arms it for
  // themselves.
  EXTERNAL_CORRESPONDENCE_IMPORT_FLAG,
  // Sweeping Sent Items so a reply written in Outlook lands on the matter
  // (VALE-1216). Ships disabled and per firm. The sweep itself is backend, so
  // what this arms on the frontend is the undo on a swept card.
  OUTLOOK_SENT_ITEMS_SYNC_FLAG,
  'vale-custody-account',
  'safe-custody',
  'zapier-integration'
] as const;

/**
 * Where the panel is allowed to render.
 *
 * It used to be local-only (`NODE_ENV !== 'development'` → null), which meant a
 * flag could only be flipped on an engineer's own machine. VALE-404 needs the
 * team to arm an in-build surface on the **deployed dev app**, so the panel now
 * renders there too.
 *
 * **This is an allow-list, not a deny-list, and that is the whole safety
 * argument.** Naming the environments the panel may appear in fails closed: a
 * new or misconfigured environment gets no panel. Excluding `'production'`
 * instead would fail open — a build where the environment could not be resolved
 * would ship a feature-flag switchboard to firms.
 *
 * `currentEnvironment` is the app's own resolution of this question
 * (`lib/config/environment.ts`), already load-bearing for auth cookie scoping,
 * so it cannot quietly drift out of step with what the deployment actually is.
 */
const DEV_TOOLS_ENVIRONMENTS: readonly string[] = ['development', 'dev-remote'];

/**
 * The second, independent allow-list: the hosts the non-production app is
 * actually served from.
 *
 * `currentEnvironment` resolves from build-time env vars set per Vercel project,
 * and this repo cannot see what the dev project has configured — if that
 * resolution came back `'production'` on `app.vale.rip`, the panel would simply
 * never appear and the feature it exists to arm would be unreachable. The host
 * is the one thing that identifies the deployment beyond doubt, so it stands
 * alongside the environment rather than instead of it. Both are positive tests
 * for non-production; `app.vale.legal` matches neither.
 */
const DEV_TOOLS_HOSTS: readonly string[] = ['.vale.rip', 'localhost'];

function isDevToolsHost(): boolean {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return DEV_TOOLS_HOSTS.some(
    (allowed) => host === allowed || host.endsWith(allowed)
  );
}

/** No-op subscribe for the hydration-safe `mounted` read below. */
const subscribeNoop = () => () => {};

export function DevFeatureFlagToggle() {
  const [open, setOpen] = useState(false);
  const { features, overrides, setFeatureOverride } = useFeatureFlags();
  const mounted = useSyncExternalStore(
    subscribeNoop,
    () => true,
    () => false
  );

  // Nothing renders until after mount. The overrides come from localStorage and
  // the host test needs `window`, so neither is knowable during SSR; rendering
  // nothing on the server and on the hydration pass keeps those two renders
  // byte-identical, which is what stops React logging a mismatch on every page
  // load with an override set.
  if (!mounted) {
    return null;
  }

  if (
    !DEV_TOOLS_ENVIRONMENTS.includes(currentEnvironment) &&
    !isDevToolsHost()
  ) {
    return null;
  }

  const hasActiveOverrides = Object.keys(overrides).length > 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          className={cn(
            // Half-size and semi-transparent at rest, full size on hover /
            // focus / while open. It floats over the bottom-right corner, which
            // is where drawer and dialog footer CTAs sit — at full size it
            // covered them. Dev-only, so this is purely local ergonomics.
            'group fixed right-4 bottom-16 z-50 h-7 w-7 rounded-full opacity-60 shadow-md transition-all',
            'hover:h-14 hover:w-14 hover:opacity-100 hover:shadow-xl',
            'focus-visible:h-14 focus-visible:w-14 focus-visible:opacity-100',
            'data-[state=open]:h-14 data-[state=open]:w-14 data-[state=open]:opacity-100',
            hasActiveOverrides && 'ring-warning opacity-100 ring-2'
          )}
          size="icon"
          title="Toggle Feature Flags"
        >
          <Flag className="h-3.5 w-3.5 transition-all group-hover:h-6 group-hover:w-6" />
        </Button>
      </PopoverTrigger>
      <PopoverContent side="top" align="end" className="w-72">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold">Feature Flags</h4>
            {hasActiveOverrides && (
              <button
                onClick={() => {
                  for (const flag of Object.keys(overrides)) {
                    setFeatureOverride(flag, null);
                  }
                }}
                className="text-muted-foreground hover:text-foreground text-xs underline"
              >
                Reset all
              </button>
            )}
          </div>
          <div className="space-y-2">
            {KNOWN_FLAGS.map((flag) => {
              const isActive = features.includes(flag);
              const hasOverride = flag in overrides;

              return (
                <div
                  key={flag}
                  className="flex items-center justify-between gap-2"
                >
                  <div className="flex items-center gap-2 overflow-hidden">
                    <div
                      className={cn(
                        'h-2 w-2 shrink-0 rounded-full',
                        isActive ? 'bg-success' : 'bg-border-strong'
                      )}
                    />
                    <span
                      className={cn(
                        'truncate text-sm',
                        hasOverride &&
                          'text-warning-subtle-foreground font-medium'
                      )}
                    >
                      {flag}
                    </span>
                  </div>
                  <Switch
                    checked={isActive}
                    onCheckedChange={(checked) =>
                      setFeatureOverride(flag, checked)
                    }
                  />
                </div>
              );
            })}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
